import { useState } from 'react'

import { cn } from '@/lib/cn'
import { mutedTextClass } from '@/components/layout/ui'

import type { ReportBucket, ReportBurndownDay, ReportFlowDay, ReportVelocity } from './api'

/*
 * Small, dependency-free SVG charts. Every one draws into a fixed viewBox and
 * scales to its container, and colours come from `currentColor` on Tailwind
 * classes so light and dark themes need no second palette.
 */

const W = 640
const H = 220
const PAD = { top: 12, right: 12, bottom: 26, left: 34 }
const PLOT_W = W - PAD.left - PAD.right
const PLOT_H = H - PAD.top - PAD.bottom

/** `2026-09-23` -> `23 Sep`, parsed as a calendar date (no timezone shift). */
export function shortDay(day: string): string {
  const [y, m, d] = day.split('-').map(Number)
  if (!y || !m || !d) return day
  return new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short', timeZone: 'UTC' }).format(
    new Date(Date.UTC(y, m - 1, d)),
  )
}

function niceMax(value: number): number {
  if (value <= 0) return 1
  if (value <= 5) return Math.ceil(value)
  const magnitude = 10 ** Math.floor(Math.log10(value))
  const step = magnitude / 2
  return Math.ceil(value / step) * step
}

function formatValue(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1)
}

function YAxis({ max, width = W }: { max: number; width?: number }) {
  const ticks = [0, max / 2, max]
  return (
    <g className="text-ink-200 dark:text-ink-800">
      {ticks.map((tick) => {
        const y = PAD.top + PLOT_H - (tick / max) * PLOT_H
        return (
          <g key={tick}>
            <line x1={PAD.left} x2={width - PAD.right} y1={y} y2={y} stroke="currentColor" strokeWidth={1} />
            <text
              x={PAD.left - 6}
              y={y + 4}
              textAnchor="end"
              className="fill-ink-400 text-[11px] dark:fill-ink-500"
            >
              {formatValue(tick)}
            </text>
          </g>
        )
      })}
    </g>
  )
}

/** Labels for the first, middle and last point of an x axis. */
function XLabels({ labels, xFor }: { labels: string[]; xFor: (index: number) => number }) {
  if (labels.length === 0) return null
  const picks = Array.from(new Set([0, Math.floor((labels.length - 1) / 2), labels.length - 1]))
  return (
    <g>
      {picks.map((index) => (
        <text
          key={index}
          x={xFor(index)}
          y={H - 6}
          textAnchor={index === 0 ? 'start' : index === labels.length - 1 ? 'end' : 'middle'}
          className="fill-ink-400 text-[11px] dark:fill-ink-500"
        >
          {labels[index]}
        </text>
      ))}
    </g>
  )
}

export function Legend({ items }: { items: { label: string; className: string; dashed?: boolean }[] }) {
  return (
    <div className={cn('flex flex-wrap items-center gap-4 text-xs', mutedTextClass)}>
      {items.map((item) => (
        <span key={item.label} className="inline-flex items-center gap-1.5">
          <span
            aria-hidden="true"
            className={cn('inline-block h-0.5 w-4', item.dashed ? 'border-t-2 border-dashed bg-transparent' : '', item.className)}
          />
          {item.label}
        </span>
      ))}
    </div>
  )
}

export function BurndownChart({ days, unit }: { days: ReportBurndownDay[]; unit: string }) {
  const [hover, setHover] = useState<number | null>(null)
  const peak = Math.max(0, ...days.map((d) => Math.max(d.ideal, d.remaining ?? 0)))
  const max = niceMax(peak)
  const step = days.length > 1 ? PLOT_W / (days.length - 1) : 0
  const xFor = (i: number) => PAD.left + (days.length > 1 ? i * step : PLOT_W / 2)
  const yFor = (v: number) => PAD.top + PLOT_H - (v / max) * PLOT_H

  const ideal = days.map((d, i) => `${xFor(i)},${yFor(d.ideal)}`).join(' ')
  const actualPoints = days
    .map((d, i) => (d.remaining === null ? null : { i, v: d.remaining }))
    .filter((p): p is { i: number; v: number } => p !== null)
  const actual = actualPoints.map((p) => `${xFor(p.i)},${yFor(p.v)}`).join(' ')
  const active = hover !== null ? days[hover] : null

  return (
    <div className="relative">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="h-auto w-full"
        role="img"
        aria-label={`Burndown in ${unit}`}
        onMouseLeave={() => setHover(null)}
      >
        <YAxis max={max} />
        <polyline
          points={ideal}
          fill="none"
          strokeWidth={2}
          strokeDasharray="5 5"
          className="stroke-ink-300 dark:stroke-ink-600"
        />
        {actualPoints.length > 0 ? (
          <polyline
            points={actual}
            fill="none"
            strokeWidth={2.5}
            strokeLinejoin="round"
            className="stroke-brand-600 dark:stroke-brand-400"
          />
        ) : null}
        {actualPoints.map((p) => (
          <circle
            key={p.i}
            cx={xFor(p.i)}
            cy={yFor(p.v)}
            r={hover === p.i ? 4.5 : 3}
            className="fill-brand-600 dark:fill-brand-400"
          />
        ))}
        {days.map((d, i) => (
          <rect
            key={d.day}
            x={xFor(i) - Math.max(step, 8) / 2}
            y={PAD.top}
            width={Math.max(step, 8)}
            height={PLOT_H}
            fill="transparent"
            onMouseEnter={() => setHover(i)}
          />
        ))}
        {hover !== null ? (
          <line
            x1={xFor(hover)}
            x2={xFor(hover)}
            y1={PAD.top}
            y2={PAD.top + PLOT_H}
            className="stroke-ink-300 dark:stroke-ink-600"
            strokeWidth={1}
          />
        ) : null}
        <XLabels labels={days.map((d) => shortDay(d.day))} xFor={xFor} />
      </svg>
      {active ? (
        <div className="pointer-events-none absolute top-1 right-2 rounded-md border border-ink-200 bg-white px-2.5 py-1.5 text-xs shadow-sm dark:border-ink-700 dark:bg-ink-900">
          <p className="font-medium">{shortDay(active.day)}</p>
          <p className={mutedTextClass}>
            Remaining: {active.remaining === null ? '—' : formatValue(active.remaining)} · Ideal:{' '}
            {formatValue(active.ideal)}
          </p>
        </div>
      ) : null}
    </div>
  )
}

export function VelocityChart({ sprints, byPoints }: { sprints: ReportVelocity[]; byPoints: boolean }) {
  const valueOf = (s: ReportVelocity) => (byPoints ? s.completed_points : s.completed_count)
  const committedOf = (s: ReportVelocity) => (byPoints ? s.committed_points : 0)
  const max = niceMax(Math.max(0, ...sprints.map((s) => Math.max(valueOf(s), committedOf(s)))))
  const slot = PLOT_W / Math.max(sprints.length, 1)
  const barW = Math.min(44, slot * 0.6)
  const xFor = (i: number) => PAD.left + slot * i + slot / 2
  const yFor = (v: number) => PAD.top + PLOT_H - (v / max) * PLOT_H
  const average = sprints.length ? sprints.reduce((sum, s) => sum + valueOf(s), 0) / sprints.length : 0

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="h-auto w-full" role="img" aria-label="Velocity per sprint">
      <YAxis max={max} />
      {sprints.map((s, i) => {
        const committed = committedOf(s)
        const value = valueOf(s)
        return (
          <g key={s.sprint_id}>
            <title>{`${s.name}: ${formatValue(value)} ${byPoints ? 'points' : 'issues'} completed${
              committed ? ` of ${formatValue(committed)} committed` : ''
            }`}</title>
            {committed > 0 ? (
              <rect
                x={xFor(i) - barW / 2}
                y={yFor(committed)}
                width={barW}
                height={PAD.top + PLOT_H - yFor(committed)}
                rx={3}
                className="fill-ink-200 dark:fill-ink-800"
              />
            ) : null}
            <rect
              x={xFor(i) - barW / 2 + (committed > 0 ? 6 : 0)}
              y={yFor(value)}
              width={barW - (committed > 0 ? 12 : 0)}
              height={PAD.top + PLOT_H - yFor(value)}
              rx={3}
              className="fill-brand-600 dark:fill-brand-400"
            />
            <text x={xFor(i)} y={yFor(value) - 5} textAnchor="middle" className="fill-ink-500 text-[11px]">
              {formatValue(value)}
            </text>
            <text x={xFor(i)} y={H - 6} textAnchor="middle" className="fill-ink-400 text-[11px] dark:fill-ink-500">
              {s.name.length > 12 ? `${s.name.slice(0, 11)}…` : s.name}
            </text>
          </g>
        )
      })}
      {sprints.length > 1 ? (
        <line
          x1={PAD.left}
          x2={W - PAD.right}
          y1={yFor(average)}
          y2={yFor(average)}
          strokeDasharray="4 4"
          strokeWidth={1.5}
          className="stroke-emerald-500"
        />
      ) : null}
    </svg>
  )
}

// The flow chart spans the full page width, so it draws into a wider viewBox
// to keep the same height as the half-width charts beside it.
const FLOW_W = 1100

export function FlowChart({ series }: { series: ReportFlowDay[] }) {
  const max = niceMax(Math.max(0, ...series.flatMap((d) => [d.created, d.resolved])))
  const slot = (FLOW_W - PAD.left - PAD.right) / Math.max(series.length, 1)
  const barW = Math.max(1.5, Math.min(10, slot * 0.36))
  const xFor = (i: number) => PAD.left + slot * i + slot / 2
  const yFor = (v: number) => PAD.top + PLOT_H - (v / max) * PLOT_H

  return (
    <svg viewBox={`0 0 ${FLOW_W} ${H}`} className="h-auto w-full" role="img" aria-label="Issues created and resolved per day">
      <YAxis max={max} width={FLOW_W} />
      {series.map((d, i) => (
        <g key={d.day}>
          <title>{`${shortDay(d.day)}: ${d.created} created, ${d.resolved} resolved`}</title>
          <rect x={xFor(i) - slot / 2} y={PAD.top} width={slot} height={PLOT_H} fill="transparent" />
          {d.created > 0 ? (
            <rect
              x={xFor(i) - barW - 0.5}
              y={yFor(d.created)}
              width={barW}
              height={PAD.top + PLOT_H - yFor(d.created)}
              rx={1.5}
              className="fill-brand-600 dark:fill-brand-400"
            />
          ) : null}
          {d.resolved > 0 ? (
            <rect
              x={xFor(i) + 0.5}
              y={yFor(d.resolved)}
              width={barW}
              height={PAD.top + PLOT_H - yFor(d.resolved)}
              rx={1.5}
              className="fill-emerald-500"
            />
          ) : null}
        </g>
      ))}
      <XLabels labels={series.map((d) => shortDay(d.day))} xFor={xFor} />
    </svg>
  )
}

/** A ranked list of horizontal bars; the widest bar is the largest bucket. */
export function BucketBars({ buckets, empty }: { buckets: ReportBucket[]; empty: string }) {
  if (buckets.length === 0) {
    return <p className={cn('text-sm', mutedTextClass)}>{empty}</p>
  }
  const max = Math.max(...buckets.map((b) => b.count))
  return (
    <ul className="space-y-2">
      {buckets.map((bucket) => (
        <li key={bucket.key} className="text-sm">
          <div className="flex items-baseline justify-between gap-3">
            <span className="truncate">{bucket.label}</span>
            <span className={cn('shrink-0 tabular-nums text-xs', mutedTextClass)}>{bucket.count}</span>
          </div>
          <div className="mt-1 h-1.5 rounded-full bg-ink-100 dark:bg-ink-800">
            <div
              className="h-1.5 rounded-full bg-brand-600 dark:bg-brand-400"
              style={{ width: `${Math.max(4, (bucket.count / max) * 100)}%` }}
            />
          </div>
        </li>
      ))}
    </ul>
  )
}
